import React, { useState } from 'react';

import DsRoot from 'src/component/ds/DsRoot';
import Specimen, { Section, Row } from 'src/screen/DesignSystem/Specimen';
import { Button } from 'src/component/ds/ui/button';
import { Input } from 'src/component/ds/ui/input';
import { Label } from 'src/component/ds/ui/label';
import { Textarea } from 'src/component/ds/ui/textarea';
import { Checkbox } from 'src/component/ds/ui/checkbox';
import { RadioGroup, RadioGroupItem } from 'src/component/ds/ui/radioGroup';
import { Switch } from 'src/component/ds/ui/switch';
import { Badge } from 'src/component/ds/ui/badge';
import { Separator } from 'src/component/ds/ui/separator';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from 'src/component/ds/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'src/component/ds/ui/tabs';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectLabel, SelectGroup
} from 'src/component/ds/ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem
} from 'src/component/ds/ui/dropdownMenu';
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from 'src/component/ds/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'src/component/ds/ui/tooltip';
import { Pill } from 'src/component/ds/ui/pill';
import { Skeleton } from 'src/component/ds/ui/skeleton';

const BUTTON_VARIANTS = ['primary', 'secondary', 'destructive', 'outline', 'ghost', 'link'] as const;
const SIZES = ['xs', 'sm', 'default', 'lg'] as const;

/*
 * Preview of every design-system component, for reviewing variants and states
 * against Figma. Gated behind REACT_APP_DESIGN_SYSTEM_ENABLED, and deliberately
 * uses no store, router or i18n so it renders standalone.
 */
export default function DesignSystemScreen () {
  const [checked, setChecked] = useState<boolean | 'indeterminate'>(true);

  return (
    <DsRoot className='ds-min-h-screen ds-bg-background ds-p-8'>
      <TooltipProvider>
        <div className='ds-mx-auto ds-flex ds-max-w-[1100px] ds-flex-col ds-gap-10'>
          <header className='ds-flex ds-flex-col ds-gap-1'>
            <h1 className='ds-text-ds-h2 ds-font-medium ds-text-foreground'>Chemelex Design System</h1>
            <p className='ds-text-ds-sm ds-text-muted-foreground'>
              Component preview. Not linked from the app.
            </p>
          </header>

          <Section title='Button'>
            {BUTTON_VARIANTS.map((variant) => (
              <Specimen key={variant} label={variant}>
                {SIZES.map((size) => (
                  <Button key={size} variant={variant} size={size}>{size}</Button>
                ))}
              </Specimen>
            ))}
            <Row>
              <Specimen label='disabled'><Button disabled>Disabled</Button></Specimen>
              <Specimen label='aria-invalid'><Button aria-invalid>Invalid</Button></Specimen>
            </Row>
          </Section>

          <Separator spacing='spacious' />

          {/* Pill composes Button, so it carries the same variant/size axes. */}
          <Section title='Pill'>
            {BUTTON_VARIANTS.map((variant) => (
              <Specimen key={variant} label={variant}>
                {SIZES.map((size) => (
                  <Pill
                    key={size}
                    label='Offline'
                    value={3}
                    testId={`pill-${variant}-${size}`}
                    variant={variant}
                    size={size}
                  />
                ))}
              </Specimen>
            ))}
            <Row>
              <Specimen label='fraction value'>
                <Pill label='Alarming' value='8/129' testId='pill-fraction' />
              </Specimen>
              <Specimen label='long label'>
                <Pill label='Forced on' value={12} testId='pill-long' />
              </Specimen>
            </Row>
          </Section>

          <Separator spacing='spacious' />

          <Section title='Skeleton'>
            <Row>
              <Specimen label='pill'>
                <Skeleton className='ds-h-ds-xs ds-w-[85px] ds-rounded-full' />
              </Specimen>
              <Specimen label='text line'>
                <Skeleton className='ds-h-4 ds-w-40' />
              </Specimen>
              <Specimen label='block'>
                <Skeleton className='ds-h-16 ds-w-32' />
              </Specimen>
            </Row>
          </Section>

          <Separator spacing='spacious' />

          <Section title='Input & Textarea'>
            <Row>
              {SIZES.map((size) => (
                <Specimen key={size} label={size}>
                  <Input size={size} placeholder={size} className='ds-w-[180px]' />
                </Specimen>
              ))}
            </Row>
            <Row>
              <Specimen label='with value'><Input defaultValue='Value' className='ds-w-[180px]' /></Specimen>
              <Specimen label='invalid'>
                {/* Row is items-end, so the message has to sit outside the flow
                  * or it lifts this cell's input above the others'. */}
                <span className='ds-relative'>
                  <Input aria-invalid aria-describedby='err' defaultValue='Bad' className='ds-w-[180px]' />
                  <span
                    id='err'
                    className='ds-absolute ds-left-0 ds-top-full ds-mt-1 ds-text-ds-mini ds-text-destructive'
                  >
                    Bad input
                  </span>
                </span>
              </Specimen>
              <Specimen label='disabled'><Input disabled placeholder='Disabled' className='ds-w-[180px]' /></Specimen>
            </Row>
            <Specimen label='textarea'>
              <Textarea placeholder='Multi-line' className='ds-w-[320px]' />
            </Specimen>
            <Specimen label='textarea invalid'>
              <Textarea aria-invalid defaultValue='Bad' className='ds-w-[320px]' />
            </Specimen>
          </Section>

          <Separator spacing='spacious' />

          <Section title='Label, Checkbox, Radio & Switch'>
            <Row>
              <Specimen label='label + checkbox'>
                {/* Figma pairs a control with its label in a 24px row. */}
                <span className='ds-flex ds-h-6 ds-items-center ds-gap-2'>
                  <Checkbox id='c1' />
                  <Label htmlFor='c1' layout='inline'>Accept terms</Label>
                </span>
              </Specimen>
              <Specimen label='checked'><Checkbox checked /></Specimen>
              <Specimen label='indeterminate'><Checkbox checked='indeterminate' /></Specimen>
              <Specimen label='invalid'><Checkbox aria-invalid /></Specimen>
              <Specimen label='disabled'><Checkbox disabled /></Specimen>
              <Specimen label='controlled'>
                <Checkbox checked={checked} onCheckedChange={setChecked} />
              </Specimen>
            </Row>
            <Specimen label='radio group'>
              <RadioGroup defaultValue='a' className='ds-flex ds-gap-4'>
                <span className='ds-flex ds-h-6 ds-items-center ds-gap-2'>
                  <RadioGroupItem value='a' id='r1' />
                  <Label htmlFor='r1' layout='inline' weight='normal'>Option A</Label>
                </span>
                <span className='ds-flex ds-h-6 ds-items-center ds-gap-2'>
                  <RadioGroupItem value='b' id='r2' />
                  <Label htmlFor='r2' layout='inline' weight='normal'>Option B</Label>
                </span>
                <span className='ds-flex ds-h-6 ds-items-center ds-gap-2'>
                  <RadioGroupItem value='c' id='r3' disabled />
                  <Label htmlFor='r3' layout='inline' weight='normal'>Disabled</Label>
                </span>
              </RadioGroup>
            </Specimen>
            <Row>
              <Specimen label='switch off'><Switch /></Specimen>
              <Specimen label='switch on'><Switch defaultChecked /></Specimen>
              <Specimen label='disabled'><Switch disabled /></Specimen>
              <Specimen label='disabled on'><Switch disabled defaultChecked /></Specimen>
            </Row>
            <Row>
              <Specimen label='label error'>
                <Label data-error='true'>Something went wrong</Label>
              </Specimen>
              <Specimen label='label normal weight'>
                <Label weight='normal'>Normal weight</Label>
              </Specimen>
            </Row>
          </Section>

          <Separator spacing='spacious' />

          <Section title='Select'>
            <Row>
              <Specimen label='default'>
                <Select>
                  <SelectTrigger className='ds-w-[200px]'>
                    <SelectValue placeholder='Select an item' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Group</SelectLabel>
                      <SelectItem value='1'>Item one</SelectItem>
                      <SelectItem value='2'>Item two</SelectItem>
                      <SelectItem value='3' disabled>Disabled item</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Specimen>
              <Specimen label='invalid'>
                <Select>
                  <SelectTrigger aria-invalid className='ds-w-[200px]'>
                    <SelectValue placeholder='Select an item' />
                  </SelectTrigger>
                  <SelectContent><SelectItem value='1'>Item one</SelectItem></SelectContent>
                </Select>
              </Specimen>
              <Specimen label='disabled'>
                <Select disabled>
                  <SelectTrigger className='ds-w-[200px]'>
                    <SelectValue placeholder='Disabled' />
                  </SelectTrigger>
                  <SelectContent><SelectItem value='1'>Item one</SelectItem></SelectContent>
                </Select>
              </Specimen>
            </Row>
          </Section>

          <Separator spacing='spacious' />

          <Section title='Badge & Separator'>
            <Specimen label='variants'>
              <Badge variant='primary'>Primary</Badge>
              <Badge variant='secondary'>Secondary</Badge>
              <Badge variant='outline'>Outline</Badge>
              <Badge variant='ghost'>Ghost</Badge>
              <Badge variant='destructive'>Destructive</Badge>
            </Specimen>
            <Specimen label='separator, vertical'>
              <span className='ds-flex ds-h-6 ds-items-center ds-text-ds-sm'>
                Left
                <Separator orientation='vertical' spacing='spacious' />
                Right
              </span>
            </Specimen>
          </Section>

          <Separator spacing='spacious' />

          <Section title='Card'>
            <Card className='ds-w-[380px]'>
              <CardHeader>
                <CardTitle>Card title</CardTitle>
                <CardDescription>A short supporting description.</CardDescription>
              </CardHeader>
              <CardContent>
                <p className='ds-text-ds-sm ds-text-foreground'>Body content sits here.</p>
              </CardContent>
              <CardFooter>
                <Button size='sm'>Confirm</Button>
                <Button size='sm' variant='ghost'>Cancel</Button>
              </CardFooter>
            </Card>
          </Section>

          <Separator spacing='spacious' />

          <Section title='Tabs'>
            <Tabs defaultValue='one' className='ds-w-[420px]'>
              <TabsList>
                <TabsTrigger value='one'>Overview</TabsTrigger>
                <TabsTrigger value='two'>Details</TabsTrigger>
                <TabsTrigger value='three' disabled>Disabled</TabsTrigger>
              </TabsList>
              <TabsContent value='one'>
                <p className='ds-text-ds-sm ds-text-foreground'>Overview panel.</p>
              </TabsContent>
              <TabsContent value='two'>
                <p className='ds-text-ds-sm ds-text-foreground'>Details panel.</p>
              </TabsContent>
            </Tabs>
          </Section>

          <Separator spacing='spacious' />

          <Section title='Overlays'>
            <Row>
              <Specimen label='dialog'>
                <Dialog>
                  <DialogTrigger asChild><Button variant='outline'>Open dialog</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Confirm action</DialogTitle>
                      <DialogDescription>This cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant='ghost'>Cancel</Button>
                      <Button variant='destructive'>Delete</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </Specimen>

              <Specimen label='dropdown menu'>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant='outline'>Open menu</Button></DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>Edit</DropdownMenuItem>
                    <DropdownMenuItem>Duplicate</DropdownMenuItem>
                    <DropdownMenuCheckboxItem checked>Show archived</DropdownMenuCheckboxItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem disabled>Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </Specimen>

              <Specimen label='tooltip'>
                <Tooltip>
                  <TooltipTrigger asChild><Button variant='outline'>Hover me</Button></TooltipTrigger>
                  <TooltipContent>Helpful text</TooltipContent>
                </Tooltip>
              </Specimen>
            </Row>
          </Section>
        </div>
      </TooltipProvider>
    </DsRoot>
  );
}
